import React, { useContext, useState, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Menu, MenuItem, Typography, Divider } from "@material-ui/core";
import { Replay, EventNote, SwapHoriz, Delete, LocalOffer, MoreVert } from "@material-ui/icons";
import useUiMode from "../../hooks/useUiMode";
import WatchLaterIcon from "@material-ui/icons/WatchLater";
import ScheduledIndividualMessageModal from "../ScheduledIndividualMessageModal";

import { i18n } from "../../translate/i18n";
import { findAllQueues } from "../../services/queue.api";
import { updateTicket, deleteTicket } from "../../services/tickets.api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import AppointmentModal from "../AppointmentModal";
import TagsModal from "../TagsModal";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import useSettings, { isSettingTruthy } from "../../hooks/useSettings";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import UndoRoundedIcon from '@material-ui/icons/UndoRounded';
import Tooltip from '@material-ui/core/Tooltip';
import { getChannelCapabilities } from "../../constants/whatsappProviders";


const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		display: "flex",
		alignItems: "center",
		gap: "6px",
		"& > *": {
			margin: 0,
		},
	},
	modernIconButton: {
		minWidth: "32px",
		height: "32px",
		borderRadius: "6px",
		border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e8e8e8'}`,
		backgroundColor: theme.mode === 'dark' ? '#374151' : '#fafafa',
		color: theme.mode === 'dark' ? '#d1d5db' : '#999999',
		transition: "all 0.2s ease",
		boxShadow: theme.mode === 'dark' 
			? '0 1px 2px rgba(0,0,0,0.2)' 
			: '0 1px 2px rgba(0,0,0,0.08)',
		"&:hover": {
			backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#f0f0f0',
			transform: "translateY(-1px)",
			boxShadow: theme.mode === 'dark' 
				? '0 2px 4px rgba(0,0,0,0.3)' 
				: '0 2px 4px rgba(0,0,0,0.12)',
			borderColor: theme.mode === 'dark' ? '#6B7280' : '#d8d8d8',
			color: theme.mode === 'dark' ? '#e5e7eb' : '#777777',
		},
		"& svg": {
			fontSize: "16px",
		},
	},
}));

const TicketActionButtonsCustom = ({ ticket }) => {
	const capacidades = getChannelCapabilities(ticket?.whatsapp?.provider);
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const { user } = useContext(AuthContext);
	const { setCurrentTicket } = useContext(TicketsContext);
	const isMounted = useRef(true);
	const { getAll } = useSettings();

	// ✅ ESTADOS PARA LOS MODALES DE ACCIONES DIRECTAS
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [closeTicketConfirmOpen, setCloseTicketConfirmOpen] = useState(false);
	const [removeTagsOnCloseEnabled, setRemoveTagsOnCloseEnabled] = useState(false);
	const [sendTicketCloseMessageEnabled, setSendTicketCloseMessageEnabled] =
		useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
	const [tagsModalOpen, setTagsModalOpen] = useState(false);
	const [simModalOpen, setSimModalOpen] = useState(false);

	// [CASO 5] Estados para el menú de Restablecimiento IA
	const [reactivateAnchorEl, setReactivateAnchorEl] = useState(null);
	const [moreAnchorEl, setMoreAnchorEl] = useState(null);
	const { isSimple } = useUiMode();
	const [aiQueues, setAiQueues] = useState([]);

	// Carga los departamentos con IA al montar (para el menú de selección)
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const { data } = await findAllQueues();
				if (!cancelled) {
					setAiQueues((data || []).filter((q) => !!q.promptId));
				}
			} catch (_e) { /* noop */ }
		})();
		return () => { cancelled = true; };
	}, []);

	const handleReactivateAi = async (queueId) => {
		setReactivateAnchorEl(null);
		setLoading(true);
		try {
			await updateTicket(ticket.id, {
				status: "open",
				aiBypass: false,
				queueId,
				userId: null
			});
		} catch (err) {
			toastError(err);
		} finally {
			if (isMounted.current) setLoading(false);
		}
	};

	// ✅ FUNCIONES PARA ACCIONES DIRECTAS
	const handleOpenAppointmentModal = () => {
		setAppointmentModalOpen(true);
	};

	const handleCloseAppointmentModal = () => {
		setAppointmentModalOpen(false);
	};

	const handleOpenTransferModal = () => {
		setTransferTicketModalOpen(true);
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	const handleOpenTagsModal = () => {
		setTagsModalOpen(true);
	};

	const handleCloseTagsModal = () => {
		if (isMounted.current) {
			setTagsModalOpen(false);
		}
	};

	const handleOpenConfirmationModal = () => {
		setConfirmationOpen(true);
	};

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const list = await getAll();
				const rowTags = list.find((s) => s.key === "removeTagsOnCloseTicket");
				const rowMsg = list.find((s) => s.key === "sendTicketCloseMessage");
				if (!cancelled) {
					setRemoveTagsOnCloseEnabled(
						rowTags ? isSettingTruthy(rowTags.value) : false
					);
					setSendTicketCloseMessageEnabled(
						rowMsg ? isSettingTruthy(rowMsg.value) : false
					);
				}
			} catch (_e) {
				/* noop */
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [getAll]);

	const handleDeleteTicket = async () => {
		try {
			await deleteTicket(ticket.id);
			
			setConfirmationOpen(false);
			
			// 🔄 ACTUALIZAR UI SIN RECARGAR PÁGINA
			if (window.location.pathname.includes(`/tickets/${ticket.id}`)) {
				window.location.href = "/tickets";
			}
			
		} catch (err) {
			toastError(err);
		}
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await updateTicket(ticket.id, {
				status: status,
				userId: userId || null,
				useIntegration: status === "closed" ? false : ticket.useIntegration,
				promptId: status === "closed" ? false : ticket.promptId,
				integrationId: status === "closed" ? false : ticket.integrationId
			});

			// ✅ VERIFICAR SI EL COMPONENTE ESTÁ MONTADO ANTES DE ACTUALIZAR ESTADO
			if (!isMounted.current) return;

		setLoading(false);
		if (status === "open") {
			setCurrentTicket({ ...ticket, code: "#open" });
		} else if (status === "closed") {
			// ✅ OPTIMIZADO: NO redirigir - confiar en eventos socket para actualizar UI
			// Esto previene la recarga de página y desconexión del socket
			setCurrentTicket({ id: null, code: null });
			// ✅ ELIMINADO: window.location.href = "/tickets" - Los eventos socket actualizarán la UI
		} else { // Este bloque manejará el status "pending" (y otros si los hubiera)
			// Aquí el ticket se movió a "pending", no redirigimos.
			// El componente Ticket (en Ticket/index.js) se actualizará vía Socket.IO
			setCurrentTicket({ id: null, code: null }); // Se puede mantener para limpiar el contexto del ticket seleccionado si aplica
		}
		} catch (err) {
			// ✅ VERIFICAR SI EL COMPONENTE ESTÁ MONTADO ANTES DE ACTUALIZAR ESTADO
			if (!isMounted.current) return;
			
			setLoading(false);
			toastError(err);
		}
	};

	const needsCloseConfirmModal =
		removeTagsOnCloseEnabled || sendTicketCloseMessageEnabled;

	const getCloseConfirmModalBody = () => {
		const tags = removeTagsOnCloseEnabled;
		const snd = sendTicketCloseMessageEnabled;
		if (tags && snd) {
			return i18n.t("ticketsList.buttons.confirmCloseBodyTagsAndMessage");
		}
		if (tags) {
			return i18n.t("ticketsList.buttons.confirmCloseWithTagsMessage");
		}
		if (snd) {
			return i18n.t("ticketsList.buttons.confirmCloseWithSendMessage");
		}
		return "";
	};

	const handleResolveClick = (e) => {
		if (needsCloseConfirmModal) {
			e?.stopPropagation?.();
			setCloseTicketConfirmOpen(true);
		} else {
			handleUpdateTicketStatus(e, "closed", user?.id);
		}
	};

	const handleConfirmCloseTicketModal = () => {
		setCloseTicketConfirmOpen(false);
		handleUpdateTicketStatus({ stopPropagation: () => {} }, "closed", user?.id);
	};

	// ✅ CLEANUP DEL COMPONENTE (unificado con carga de settings)
	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	return (
		<>
		<div className={classes.actionButtons}>
			{ticket.status === "closed" && (
				<>
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
				</>
			)}
			{ticket.status === "open" && !isSimple && (
				<>
					<Tooltip title={i18n.t("messagesList.header.buttons.return")}>
						<IconButton 
							onClick={e => handleUpdateTicketStatus(e, "pending", null)}
							className={classes.modernIconButton}
						>
							<UndoRoundedIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title={i18n.t("messagesList.header.buttons.resolve")}>
						<IconButton 
							onClick={handleResolveClick}
							className={classes.modernIconButton}
						>
							<CheckCircleIcon />
						</IconButton>
					</Tooltip>
					{/* ✅ BOTONES DE ACCIÓN DIRECTA - REEMPLAZANDO MENÚ DE TRES PUNTOS */}
					{capacidades.schedule && (<>
					<Tooltip title={i18n.t("ticketOptionsMenu.schedule")}>
						<IconButton 
							onClick={handleOpenAppointmentModal}
							className={classes.modernIconButton}
						>
							<EventNote />
						</IconButton>
					</Tooltip>
					<Tooltip title="Programar mensaje individual">
						<IconButton
							onClick={() => setSimModalOpen(true)}
							className={classes.modernIconButton}
							aria-label="Programar mensaje individual"
						>
							<WatchLaterIcon />
						</IconButton>
					</Tooltip>
					</>)}
					<Tooltip title={i18n.t("ticketOptionsMenu.transfer")}>
						<IconButton 
							onClick={handleOpenTransferModal}
							className={classes.modernIconButton}
						>
							<SwapHoriz />
						</IconButton>
					</Tooltip>
					<Tooltip title="Etiquetas">
						<IconButton
							onClick={handleOpenTagsModal}
							className={classes.modernIconButton}
						>
							<LocalOffer />
						</IconButton>
					</Tooltip>
					{/* ✅ Solo mostrar botón "Eliminar" para administradores */}
					{user?.profile === 'admin' && (
					<Tooltip title={i18n.t("ticketOptionsMenu.delete")}>
						<IconButton
							onClick={handleOpenConfirmationModal}
							className={classes.modernIconButton}
						>
							<Delete />
						</IconButton>
					</Tooltip>
					)}
					{/* [CASO 5] Botón Restablecer IA — solo cuando el operador tiene el chat abierto y aiBypass activo */}
					{ticket.aiBypass && (
						<Tooltip title="Restablecer Agente IA">
							<IconButton
								onClick={(e) => setReactivateAnchorEl(e.currentTarget)}
								className={classes.modernIconButton}
								style={{ color: '#f59e0b', borderColor: '#f59e0b' }}
								aria-label="Restablecer Agente IA"
							>
								<span role="img" aria-label="robot" style={{ fontSize: 16 }}>🤖</span>
							</IconButton>
						</Tooltip>
					)}
				</>
			)}
			{ticket.status === "open" && isSimple && (
				<>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="outlined"
						color="primary"
						startIcon={<CheckCircleIcon />}
						onClick={handleResolveClick}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner>
					<Tooltip title={i18n.t("ticketOptionsMenu.transfer")}>
						<IconButton size="small" onClick={handleOpenTransferModal} aria-label={i18n.t("ticketOptionsMenu.transfer")}>
							<SwapHoriz />
						</IconButton>
					</Tooltip>
					{ticket.aiBypass && (
						<Tooltip title="Restablecer Agente IA">
							<IconButton
								onClick={(e) => setReactivateAnchorEl(e.currentTarget)}
								className={classes.modernIconButton}
								style={{ color: '#f59e0b', borderColor: '#f59e0b' }}
								aria-label="Restablecer Agente IA"
							>
								<span role="img" aria-label="robot" style={{ fontSize: 16 }}>🤖</span>
							</IconButton>
						</Tooltip>
					)}
					<IconButton size="small" onClick={(e) => setMoreAnchorEl(e.currentTarget)} aria-label={i18n.t("tickets.simple.more")}>
						<MoreVert />
					</IconButton>
					<Menu anchorEl={moreAnchorEl} open={Boolean(moreAnchorEl)} onClose={() => setMoreAnchorEl(null)}>
						<MenuItem onClick={(e) => { setMoreAnchorEl(null); handleUpdateTicketStatus(e, "pending", null); }}>
							{i18n.t("messagesList.header.buttons.return")}
						</MenuItem>
						{capacidades.schedule && (
							<MenuItem onClick={() => { setMoreAnchorEl(null); handleOpenAppointmentModal(); }}>
								{i18n.t("ticketOptionsMenu.schedule")}
							</MenuItem>
						)}
						{capacidades.schedule && (
							<MenuItem onClick={() => { setMoreAnchorEl(null); setSimModalOpen(true); }}>
								{i18n.t("tickets.simple.scheduleMessage")}
							</MenuItem>
						)}
						<MenuItem onClick={() => { setMoreAnchorEl(null); handleOpenTagsModal(); }}>
							{i18n.t("tickets.simple.tags")}
						</MenuItem>
						{user?.profile === 'admin' && (
							<MenuItem onClick={() => { setMoreAnchorEl(null); handleOpenConfirmationModal(); }} style={{ color: '#B91C1C' }}>
								{i18n.t("ticketOptionsMenu.delete")}
							</MenuItem>
						)}
					</Menu>
				</>
			)}
			{ticket.status === "pending" && (
				<>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.accept")}
					</ButtonWithSpinner>
					{ticket.uuid && capacidades.schedule ? (
						<Tooltip title={i18n.t("ticketOptionsMenu.schedule")}>
							<IconButton
								onClick={handleOpenAppointmentModal}
								className={classes.modernIconButton}
								aria-label={i18n.t("ticketOptionsMenu.schedule")}
							>
								<EventNote />
							</IconButton>
						</Tooltip>
					) : null}
					{/* [CASO 5] Botón ámbar en pending — el supervisor reactiva la IA sin aceptar el ticket */}
					{ticket.aiBypass && (
						<Tooltip title="Restablecer Agente IA">
							<IconButton
								onClick={(e) => setReactivateAnchorEl(e.currentTarget)}
								className={classes.modernIconButton}
								style={{ color: '#f59e0b', borderColor: '#f59e0b' }}
								aria-label="Restablecer Agente IA"
							>
								<span role="img" aria-label="robot" style={{ fontSize: 16 }}>🤖</span>
							</IconButton>
						</Tooltip>
					)}
				</>
			)}
		</div>

		{/* [CASO 5] Menú de selección de Agente IA para restablecer */}
		<Menu
			anchorEl={reactivateAnchorEl}
			open={Boolean(reactivateAnchorEl)}
			onClose={() => setReactivateAnchorEl(null)}
			PaperProps={{ style: { minWidth: 220 } }}
		>
			<Typography variant="caption" style={{ padding: '6px 16px', display: 'block', color: '#6B7280', fontWeight: 600 }}>
				Selecciona el Agente IA
			</Typography>
			<Divider />
			{aiQueues.length === 0 && (
				<MenuItem disabled>Sin agentes IA disponibles</MenuItem>
			)}
			{aiQueues.map((q) => (
				<MenuItem key={q.id} onClick={() => handleReactivateAi(q.id)}>
					🤖 {q.name}
				</MenuItem>
			))}
		</Menu>

		{/* ✅ MODALES PARA LAS ACCIONES DIRECTAS */}
		<ConfirmationModal
			title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
				ticket.id
			} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
				ticket.contact?.name || i18n.t("ticketOptionsMenu.confirmationModal.unknownContact")
			}?`}
			open={confirmationOpen}
			onClose={setConfirmationOpen}
			onConfirm={handleDeleteTicket}
		>
			{i18n.t("ticketOptionsMenu.confirmationModal.message")}
		</ConfirmationModal>
		<ConfirmationModal
			title={i18n.t("ticketsList.buttons.confirmCloseWithTagsTitle")}
			open={closeTicketConfirmOpen}
			onClose={() => setCloseTicketConfirmOpen(false)}
			onConfirm={handleConfirmCloseTicketModal}
		>
			{getCloseConfirmModalBody()}
		</ConfirmationModal>
		<TransferTicketModalCustom
			modalOpen={transferTicketModalOpen}
			onClose={handleCloseTransferTicketModal}
			ticketid={ticket.id}
		/>
		<AppointmentModal
			open={appointmentModalOpen}
			onClose={handleCloseAppointmentModal}
			appointment={null}
			ticketId={ticket.id}
			initialWhatsappId={ticket.whatsappId}
			initialContact={ticket.contact}
			onSaved={() => {}}
		/>
		<TagsModal
			open={tagsModalOpen}
			onClose={handleCloseTagsModal}
			ticketId={ticket.id}
			ticket={ticket}
		/>
		<ScheduledIndividualMessageModal
			open={simModalOpen}
			onClose={() => setSimModalOpen(false)}
			ticketId={ticket.id}
		/>
	</>
	);
};

export default TicketActionButtonsCustom;
